import React from 'react';
import { BarChart, Bar, XAxis, ResponsiveContainer, ReferenceLine } from 'recharts';
import { styled } from '@mui/material/styles';
import ReportNasemScoreBadge from '../../../reports/ReportNasemScoreBadge';
import { DemographicChartProps } from '../../../types/reports';
import { findPercentile, getExposureText, getSelectedData, getValueForPercentile } from '../../../utils/reports';
import { Box } from '@mui/material';
import ReportNasemScoreBadgeAndText from '../../../reports/ReportNasemScoreBadgeAndText';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

interface CustomDoubleLabelProps {
  viewBox: {
    x: number;
    y: number;
    width: number;
  };
  valueLeft: string;
  valueRight: string;
}

const CHART_HEIGHT = 400;
const BAR_WIDTH = 80;
const CHART_PADDING = 10;

interface BadgeContainerProps {
  top: number;
}

const BadgeContainer = styled('div')<BadgeContainerProps>(({ top }) => ({
  position: 'absolute',
  top: `${top}px`,
  transform: 'translateY(-50%)',
  zIndex: 10,
  left: '50px',
}));

const DemographicBarChart: React.FC<DemographicChartProps> = ({
  reportDemographicData,
  nasemScore,
  selectedFilter,
  dataset,
  hasAboveRange,
}) => {
  const { theme } = useThemeContext();
  if (!reportDemographicData) {
    return null;
  }
  const data = getSelectedData(reportDemographicData, dataset, selectedFilter);
  if (!data) {
    return null;
  }

  // Custom label for the reference lines
  const CustomDoubleLabel = ({ viewBox, valueLeft, valueRight }: CustomDoubleLabelProps) => {
    const { x, y, width } = viewBox;
    return (
      <>
        {/* Left Label */}
        <text
          x={x + 15}
          y={y + 4}
          textAnchor="end"
          style={{
            fill: theme.custom.report.comparison.barChart.customDoubleLabel.leftFill,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {valueLeft}
        </text>
        {/* Right Label */}
        <text
          x={x + width - 50}
          y={y + 4}
          textAnchor="start"
          style={{
            fill: theme.custom.report.comparison.barChart.customDoubleLabel.rightFill,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {valueRight}
        </text>
      </>
    );
  };

  const barsData = [
    {
      low: findPercentile(2.0, data), // Green bar ends at 2 ng/mL
      medium: findPercentile(20.0, data) - findPercentile(2.0, data), // Yellow bar ends at 20 ng/mL
      high: 100 - findPercentile(20.0, data), // Red bar covers the rest
    },
  ];

  const userPercent = findPercentile(nasemScore, data);
  const renderedHeight = CHART_HEIGHT - 2 * CHART_PADDING;
  const badgeTopPosition = CHART_PADDING + renderedHeight * (1 - userPercent / 100);

  // Reference lines configuration for various percentages
  const referenceLines = [
    { y: findPercentile(2.0, data), leftLabel: `${findPercentile(2.0, data)}%`, rightLabel: '2 ng/mL' },
    { y: 50, leftLabel: '50%', rightLabel: `${getValueForPercentile(50, data)} ng/mL` },
    { y: findPercentile(20.0, data), leftLabel: `${findPercentile(20.0, data)}%`, rightLabel: '20 ng/mL' },
    { y: 100, leftLabel: '100%', rightLabel: `${getValueForPercentile(100, data)} ng/mL` },
  ];

  const { exposureTextColor } = getExposureText(userPercent);

  const renderBarChart = () => {
    return (
      <div style={{ position: 'relative', width: '100%', height: CHART_HEIGHT }}>
        <ResponsiveContainer>
          <BarChart data={barsData} layout="horizontal" margin={{ top: 10, right: 30, left: 40, bottom: 20 }}>
            <XAxis type="category" dataKey="name" hide />

            {referenceLines.map((line, index) => (
              <ReferenceLine
                key={index}
                y={line.y}
                stroke={theme.custom.report.comparison.barChart.referenceLines.stroke}
                strokeWidth={2}
                label={
                  <CustomDoubleLabel
                    viewBox={{
                      x: -20,
                      y: (CHART_HEIGHT * (100 - line.y)) / 100,
                      width: 400,
                    }}
                    valueLeft={line.leftLabel}
                    valueRight={line.rightLabel}
                  />
                }
                ifOverflow="visible"
                clipPath="inset(0px 60px 0px 30px)"
              />
            ))}

            <Bar
              dataKey="low"
              stackId="1"
              fill={theme.custom.report.comparison.barChart.bar.low.fill}
              barSize={BAR_WIDTH}
            />
            <Bar
              dataKey="medium"
              stackId="1"
              fill={theme.custom.report.comparison.barChart.bar.medium.fill}
              barSize={BAR_WIDTH}
            />
            <Bar
              dataKey="high"
              stackId="1"
              fill={theme.custom.report.comparison.barChart.bar.high.fill}
              barSize={BAR_WIDTH}
              radius={[8, 8, 0, 0]}
            />

            <ReferenceLine
              y={userPercent}
              stroke={exposureTextColor}
              strokeDasharray="3 3"
              strokeWidth={2}
              ifOverflow="visible"
              clipPath="inset(0px 60px 0px 20px)"
            />

            <ReferenceLine
              y={0.0}
              stroke={theme.custom.report.comparison.barChart.referenceLines.y0.stroke}
              strokeWidth={2}
              label={{
                value: '0.0',
                position: 'left',
                fill: theme.custom.report.comparison.barChart.referenceLines.y0.label.fill,
                fontSize: 14,
                fontWeight: 400,
                textAnchor: 'end',
              }}
            />
          </BarChart>
        </ResponsiveContainer>

        <BadgeContainer top={badgeTopPosition}>
          <ReportNasemScoreBadge nasemScore={nasemScore} />
        </BadgeContainer>
      </div>
    );
  };

  return (
    <Box>
      {renderBarChart()}
      <ReportNasemScoreBadgeAndText
        nasemScore={nasemScore}
        userPercentile={userPercent}
        showBadge={false}
        hasAboveRange={hasAboveRange}
      />
    </Box>
  );
};

export default DemographicBarChart;
